import { Component, inject, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { VendorService } from '../vendor.service';
import { ManufacturerService } from '../../manufacturers/manufacturers.service';
import { CommonModule } from '@angular/common';
import { NgSelectComponent } from '@ng-select/ng-select';
import { debounceTime, distinctUntilChanged, Subject } from 'rxjs';
import { ConfirmDialogService } from '../../confirm-dialog/confirm-dialog.service';

@Component({
  selector: 'app-add-vendor',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, NgSelectComponent],
  templateUrl: './add-vendor.component.html',
  styleUrl: './add-vendor.component.scss',
})
export class AddVendorComponent implements OnInit {
  private fb = inject(FormBuilder);
  private vendorService = inject(VendorService);
  private manufacturerService = inject(ManufacturerService);
  private toastr = inject(ToastrService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private confirmDialog = inject(ConfirmDialogService);

  vendorForm: FormGroup;
  isEdit = false;
  vendorId: any;
  isSaved = false;

  // Manufacturer search state
  existingManufacturerIds: any = [];
  allManufacturers: any[] = [];
  manufacturers: any[] = [];
  selectedManufacturers: any[] = [];
  searchingManufacturers = false;
  private manufacturerSearch$ = new Subject<string>();

  // GSTIN Validation state
  gstinValid: boolean | null = null;
  validatingGstin = false;

  constructor() {
    this.vendorForm = this.fb.group({
      name: ['', [Validators.required, Validators.maxLength(100)]],
      vendorCode: ['', [Validators.required, Validators.maxLength(10)]],
      contactPerson: ['', [Validators.required, Validators.maxLength(50)]],
      contactPhone: ['', [Validators.required, Validators.maxLength(15)]],
      contactEmail: [
        '',
        [Validators.required, Validators.email, Validators.maxLength(100)],
      ],
      address: ['', [Validators.required, Validators.maxLength(250)]],
      city: ['', Validators.required],
      state: ['', Validators.required],
      pincode: ['', [Validators.required, Validators.pattern(/^[0-9]{6}$/)]],
      country: ['India', Validators.required],
      gstin: [
        '',
        [
          Validators.required,
          Validators.pattern(
            /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/
          ),
        ],
      ],
      panNumber: ['', [Validators.required, Validators.maxLength(10)]],
      fssaiLicensed: [false],
      tdsApplicable: [false, Validators.required],
      vendorType: ['', Validators.required],
      paymentTerms: ['', Validators.required],
      preferredCurrency: ['INR', Validators.required],
      rating: [0, [Validators.min(0), Validators.max(5)]],
      documentsUploaded: [false],
      notes: ['', Validators.maxLength(200)],
      bankAccountName: ['', [Validators.required, Validators.maxLength(100)]],
      bankAccountNumber: ['', [Validators.required, Validators.maxLength(30)]],
      ifsc: ['', [Validators.required, Validators.maxLength(15)]],
      isActive: [true, Validators.required],
      // Default cost-price convention for POs against this vendor.
      // false = basic + GST extra (B2B standard); true = inclusive (CGST Rule 35).
      costPriceIncludesGst: [false],
      cin: [''],
      constitution: ['', Validators.required],
      isMSME: [false],
      msmeRegistrationType: [''],
      msmeRegistrationNumber: [''],
      manufacturerIds: [[], Validators.required],
      dayOfWeek: [],
      dayOfMonth: [],
    });

    // Local search subscription
    this.manufacturerSearch$
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe((term) => {
        const normalized = term.trim().toLowerCase();

        if (!normalized || normalized.length < 2) {
          this.manufacturers = [...this.allManufacturers]; // reset to all
          return;
        }

        this.manufacturers = this.allManufacturers.filter((m) =>
          m.name.toLowerCase().includes(normalized)
        );
      });
  }

  ngOnInit(): void {
    this.vendorId = this.route.snapshot.paramMap.get('id');
    this.isEdit = !!this.vendorId;

    if (this.isEdit) {
      this.vendorService.getVendorById(this.vendorId!).subscribe((vendor) => {
        this.vendorForm.patchValue(vendor);
        const manufacturerIds =
          (vendor as any).manufacturerVendors?.map(
            (mv: any) => mv.manufacturer?.id
          ) || [];

        // Store IDs in form control (needed for ng-select value)
        this.vendorForm.get('manufacturerIds')?.setValue(manufacturerIds);

        this.existingManufacturerIds = manufacturerIds;

        if (this.allManufacturers.length) {
          this.loadSelectedManufacturers(manufacturerIds);
        }
      });
    }

    this.fetchManufacturers();
    this.setupDateExclusivity();

    this.vendorForm.markAsPristine();
    this.vendorForm.markAsUntouched();

    this.vendorForm.get('gstin')?.valueChanges.subscribe(() => {
      this.gstinValid = null;
    });
  }

  setupDateExclusivity(): void {
    const dayOfWeekControl = this.vendorForm.get('dayOfWeek');
    const dayOfMonthControl = this.vendorForm.get('dayOfMonth');

    // Watch for changes in Day of Week
    dayOfWeekControl?.valueChanges
      .pipe(distinctUntilChanged())
      .subscribe((value: any[]) => {
        // Check if Day of Week has any selections
        if (value && value.length > 0) {
          // If selected, disable and clear Day of Month
          dayOfMonthControl?.disable({ emitEvent: false });
          dayOfMonthControl?.setValue([], { emitEvent: false }); // Clear value silently
        } else {
          // If no selection, enable Day of Month (but only if it's not disabled by its own logic)
          dayOfMonthControl?.enable({ emitEvent: false });
        }
      });

    // Watch for changes in Day of Month
    dayOfMonthControl?.valueChanges
      .pipe(distinctUntilChanged())
      .subscribe((value: any[]) => {
        // Check if Day of Month has any selections
        if (value && value.length > 0) {
          // If selected, disable and clear Day of Week
          dayOfWeekControl?.disable({ emitEvent: false });
          dayOfWeekControl?.setValue([], { emitEvent: false }); // Clear value silently
        } else {
          // If no selection, enable Day of Week
          dayOfWeekControl?.enable({ emitEvent: false });
        }
      });
  }

  loadSelectedManufacturers(manufacturerIds: string[]): void {
    this.selectedManufacturers = this.allManufacturers.filter((m) =>
      manufacturerIds.includes(m.id)
    );
  }

  fetchManufacturers(): void {
    this.searchingManufacturers = true;

    this.manufacturerService.getManufacturers({ limit: 1000 }).subscribe({
      next: (res: any) => {
        this.allManufacturers = [...(res?.data || [])];
        this.manufacturers = [...this.allManufacturers];
        this.searchingManufacturers = false;
        if (
          this.isEdit &&
          this.vendorForm.get('manufacturerIds')?.value?.length
        ) {
          const manIds: string[] =
            this.vendorForm.get('manufacturerIds')?.value;
          this.loadSelectedManufacturers(manIds);
        }
      },
      error: (err) => {
        console.error('Fetch failed:', err);
        this.allManufacturers = [];
        this.manufacturers = [];
        this.searchingManufacturers = false;
      },
    });
  }

  goBack() {
    this.router.navigate(['/vendors']);
  }

  saveVendor() {
    this.vendorForm.markAllAsTouched();

    if (this.vendorForm.valid) {
      const cleanedFormValue = Object.fromEntries(
        Object.entries(this.vendorForm.getRawValue()).filter(
          ([_, v]) => v !== ''
        )
      );

      cleanedFormValue['manufacturerIds'] = this.selectedManufacturers.map(
        (m) => m.id
      );

      delete cleanedFormValue['isMSME'];

      const request = this.isEdit
        ? this.vendorService.updateVendor(this.vendorId, cleanedFormValue)
        : this.vendorService.addVendor(cleanedFormValue);

      request.subscribe({
        next: (res: any) => {
          if (res) {
            this.isSaved = true;
            this.toastr.success(
              this.isEdit
                ? 'Vendor updated successfully'
                : 'Vendor saved successfully'
            );
            this.goBack();
          } else {
            this.toastr.error(
              this.isEdit ? 'Vendor update failed!' : 'Vendor save failed!'
            );
          }
        },
        error: (err) => {
          console.error('Save failed:', err);
          this.toastr.error(
            this.isEdit ? 'Vendor update failed!' : 'Vendor save failed!'
          );
        },
      });
    } else {
      this.vendorForm.markAllAsTouched();
    }
  }

  onMSMEToggle(event: any) {
    const isMSME = event.target.checked;

    if (isMSME) {
      this.vendorForm
        .get('msmeRegistrationType')
        ?.setValidators([Validators.required]);
      this.vendorForm
        .get('msmeRegistrationNumber')
        ?.setValidators([Validators.required]);
    } else {
      this.vendorForm.get('msmeRegistrationType')?.clearValidators();
      this.vendorForm.get('msmeRegistrationNumber')?.clearValidators();

      // reset values
      this.vendorForm.patchValue({
        msmeRegistrationType: null,
        msmeRegistrationNumber: null,
      });

      // remove from raw value (so it won't be sent in payload)
      const { msmeRegistrationType, msmeRegistrationNumber, ...rest } =
        this.vendorForm.getRawValue();
      this.vendorForm.patchValue(rest, { emitEvent: false });
    }

    this.vendorForm.get('msmeRegistrationType')?.updateValueAndValidity();
    this.vendorForm.get('msmeRegistrationNumber')?.updateValueAndValidity();
  }

  onManufacturerSearchInput(event: { term: string; items: any[] }): void {
    const input = event.term.trim();
    this.manufacturerSearch$.next(input);
  }

  selectManufacturer(manufacturer: any): void {
    const alreadySelected = this.selectedManufacturers.find(
      (m) => m.id === manufacturer.id
    );

    if (!alreadySelected) {
      this.selectedManufacturers.push(manufacturer);
    } else {
      this.toastr.warning('Manufacturer already added.');
    }
  }

  removeManufacturer(index: number): void {
    const removed = this.selectedManufacturers.splice(index, 1)[0];
    const manufacturerIdsControl = this.vendorForm.get('manufacturerIds');
    const currentIds = manufacturerIdsControl?.value || [];
    manufacturerIdsControl?.setValue(
      currentIds.filter((id: string) => id !== removed.id)
    );
  }
  selectManufacturerFromControl(): void {
    const selectedId = this.vendorForm.get('manufacturerIds')?.value;
    const selectedManufacturer = this.manufacturers.find(
      (m) => m.id === selectedId
    );

    if (selectedManufacturer) {
      this.selectManufacturer(selectedManufacturer);
    } else {
      this.toastr.error('Please select a manufacturer to add.');
    }
  }

  // async canDeactivate(): Promise<boolean> {
  //   // 🌟 FIX 3: Check if the form is dirty AND not saved 🌟
  //   if (this.isSaved || this.vendorForm.pristine) {
  //     // If saved or pristine, allow navigation without confirmation
  //     return true;
  //   }

  //   // If dirty and not saved, show the confirmation dialog
  //   const action = this.isEdit ? 'Edit' : 'Create';
  //   return this.confirmDialog.confirm({
  //     title: 'Discard changes?',
  //     message: `You have unsaved changes in the <strong>${action} Vendor</strong> page. Are you sure you want to leave this page?`,
  //     confirmText: 'Leave',
  //     cancelText: 'Stay',
  //     size: 'lg',
  //   });
  // }
  // src/app/features/vendors/add-vendor/add-vendor.component.ts

  // ... (inside AddVendorComponent class)

  async canDeactivate(): Promise<boolean> {
    // 1. Check only if the changes have been successfully saved.
    // If it's saved, always allow navigation.
    if (this.isSaved) {
      return true;
    }

    // 2. If it is NOT saved, proceed to show the confirmation dialog,
    // REGARDLESS of whether the form is pristine or dirty.
    const action = this.isEdit ? 'Edit' : 'Create';
    return this.confirmDialog.confirm({
      title: 'Discard changes?',
      message: `You have unsaved changes in the <strong>${action} Vendor</strong> page. Are you sure you want to leave this page?`,
      confirmText: 'Leave',
      cancelText: 'Stay',
      size: 'lg',
    });
  }

  validateGstin() {
    const gstin = this.vendorForm.get('gstin')?.value;
    if (!gstin) return;

    this.validatingGstin = true;
    this.vendorService.validateGstin(gstin).subscribe({
      next: (res: any) => {
        this.validatingGstin = false;

        // Check if company_status is 'Active'
        if (res?.company_details?.company_status === 'Active') {
          this.gstinValid = true;
          this.toastr.success('GSTIN is valid');

          // Populate form fields
          const details = res.company_details;
          this.vendorForm.patchValue({
            name: details.legal_name,
            panNumber: details.pan,
            address: details.pradr?.addr,
            city: details.pradr?.loc,
            state: details.state_info?.name,
            pincode: details.pradr?.pincode,
          });
        } else {
          this.gstinValid = false;
          this.toastr.error('GSTIN is invalid or not Active');
        }
      },
      error: (err) => {
        this.validatingGstin = false;
        this.gstinValid = false;
        this.toastr.error('Failed to validate GSTIN');
        console.error(err);
      },
    });
  }
}
